import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	MenuItem,
	Paper,
	TextField,
	Typography,
} from "@mui/material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AppRouterConfig from "../../../AppRouterConfig";

const API = process.env.REACT_APP_LAMBDA_ARTEH_API_DOMAIN;

/**
 * Disclosure workspace (backlog A-02): author the narrative pillars of the
 * AASB S2 disclosure. Sections left empty render as guided placeholders in
 * the report; every save keeps a version so earlier drafts can be restored.
 */
const SECTIONS = [
	{
		key: "governance",
		title: "Part 1 — Governance",
		guide: [
			"The governance body (board or committee) responsible for oversight of climate-related risks and opportunities, and how its responsibilities are reflected in its terms of reference.",
			"How the body ensures the right skills are available, how often it is informed, and how it considers climate matters in strategy, major transactions and risk management.",
			"Management's role, delegated controls and procedures, and how they integrate with other internal functions.",
		],
	},
	{
		key: "strategy",
		title: "Part 2 — Strategy",
		guide: [
			"Climate-related risks and opportunities that could reasonably be expected to affect the entity's prospects, and the time horizons over which they may occur.",
			"Current and anticipated effects on the business model, value chain, strategy and decision-making, including the transition plan and any climate targets.",
			"Effects on financial position, performance and cash flows for the reporting period and anticipated effects.",
			"Climate resilience, assessed using climate-related scenario analysis across at least two scenarios including a 1.5°C scenario.",
		],
	},
	{
		key: "risk",
		title: "Part 3 — Risk management",
		guide: [
			"The processes and policies used to identify, assess, prioritise and monitor climate-related risks, including the inputs, parameters and scenario analysis used.",
			"How the entity prioritises climate-related risks relative to other risks, and how those processes are integrated into the overall risk management process.",
			"The processes used to identify, assess, prioritise and monitor climate-related opportunities.",
		],
	},
	{
		key: "controls",
		title: "Part 5 — Internal controls (optional)",
		guide: [
			"How emissions data is collected, by whom, and the systems used (the ARTEH platform as the system of record).",
			"Review and sign-off cadence for activity data, emission factors and the final figures.",
			"How errors are detected and corrected, and how changes are tracked (audit trail, versioned baselines and narratives).",
		],
	},
] as const;

type SectionKey = (typeof SECTIONS)[number]["key"];

interface SectionMeta {
	version: number;
	updatedBy: string | null;
	updatedAt: string;
}

interface HistoryVersion {
	version: number;
	content: string;
	updatedBy: string | null;
	createdAt: string;
}

const emptyDrafts = (): Record<SectionKey, string> => ({
	governance: "",
	strategy: "",
	risk: "",
	controls: "",
});

const DisclosureWorkspace: React.FC = () => {
	const { getAccessTokenSilently } = useAuth0();
	const currentYear = new Date().getFullYear();
	const [year, setYear] = useState<number>(currentYear - 1);
	const [loading, setLoading] = useState(true);
	const [available, setAvailable] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [drafts, setDrafts] = useState<Record<SectionKey, string>>(emptyDrafts());
	const [saved, setSaved] = useState<Record<SectionKey, string>>(emptyDrafts());
	const [meta, setMeta] = useState<Partial<Record<SectionKey, SectionMeta>>>({});
	const [busy, setBusy] = useState<SectionKey | null>(null);
	const [history, setHistory] = useState<{
		section: SectionKey;
		title: string;
		loading: boolean;
		versions: HistoryVersion[];
	} | null>(null);

	const request = useCallback(
		async (path: string, init?: RequestInit) => {
			const token = await getAccessTokenSilently();
			const res = await fetch(`${API}${path}`, {
				...init,
				headers: {
					Authorization: `Bearer ${token}`,
					"Content-Type": "application/json",
					...(init?.headers || {}),
				},
			});
			const json = await res.json().catch(() => ({}));
			if (!res.ok || json.Ok === false)
				throw new Error(json.Message || "Request failed");
			return json.Data ?? json;
		},
		[getAccessTokenSilently],
	);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			setLoading(true);
			setError(null);
			try {
				const data = await request(
					`/api/disclosure/narratives?year=${year}`,
				);
				if (cancelled) return;
				setAvailable(data.available !== false);
				const nextDrafts = emptyDrafts();
				const nextMeta: Partial<Record<SectionKey, SectionMeta>> = {};
				for (const { key } of SECTIONS) {
					const section = data.sections?.[key];
					if (section) {
						nextDrafts[key] = section.content || "";
						nextMeta[key] = {
							version: section.version,
							updatedBy: section.updatedBy,
							updatedAt: section.updatedAt,
						};
					}
				}
				setDrafts(nextDrafts);
				setSaved({ ...nextDrafts });
				setMeta(nextMeta);
			} catch (e: any) {
				if (!cancelled)
					setError(e.message || "Could not load the narratives");
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [year, request]);

	const save = async (key: SectionKey) => {
		setBusy(key);
		setError(null);
		try {
			const data = await request(`/api/disclosure/narratives`, {
				method: "PUT",
				body: JSON.stringify({ year, section: key, content: drafts[key] }),
			});
			setSaved((prev) => ({ ...prev, [key]: drafts[key] }));
			setMeta((prev) => ({
				...prev,
				[key]: {
					version: data.version,
					updatedBy: data.updatedBy,
					updatedAt: data.updatedAt,
				},
			}));
		} catch (e: any) {
			setError(e.message || "Could not save the narrative");
		} finally {
			setBusy(null);
		}
	};

	const openHistory = async (key: SectionKey, title: string) => {
		setHistory({ section: key, title, loading: true, versions: [] });
		try {
			const data = await request(
				`/api/disclosure/narratives/history?year=${year}&section=${key}`,
			);
			setHistory({
				section: key,
				title,
				loading: false,
				versions: data.versions || [],
			});
		} catch (e: any) {
			setHistory(null);
			setError(e.message || "Could not load the history");
		}
	};

	const restore = (version: HistoryVersion) => {
		if (!history) return;
		setDrafts((prev) => ({ ...prev, [history.section]: version.content }));
		setHistory(null);
	};

	const formatWhen = (iso: string) => {
		const date = new Date(iso);
		return isNaN(date.getTime()) ? iso : date.toLocaleString();
	};

	const years = Array.from({ length: 6 }, (_, i) => currentYear - i);

	return (
		<Box sx={{ maxWidth: 900, mx: "auto", px: 2, py: 3 }}>
			<Typography variant="h4" gutterBottom>
				Disclosure workspace
			</Typography>
			<Typography color="text.secondary" sx={{ mb: 2 }}>
				Author the Governance, Strategy and Risk management narratives
				for the AASB S2 disclosure. Saved narratives replace the guided
				placeholders in the report and its PDF; every save keeps a
				version you can restore.
			</Typography>

			<Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 3 }}>
				<TextField
					select
					size="small"
					label="Reporting year"
					value={year}
					onChange={(e) => setYear(parseInt(e.target.value, 10))}
					sx={{ width: 160 }}
				>
					{years.map((y) => (
						<MenuItem key={y} value={y}>
							{y}
						</MenuItem>
					))}
				</TextField>
				<Button
					component={Link}
					to={AppRouterConfig.USERCOMPANY.REPORT.S2REPORT}
					startIcon={<DescriptionOutlinedIcon />}
				>
					View the S2 report
				</Button>
				{loading && <CircularProgress size={20} />}
			</Box>

			{error && (
				<Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
					{error}
				</Alert>
			)}
			{!available && !loading && (
				<Alert severity="warning" sx={{ mb: 2 }}>
					Narrative storage is not provisioned on this environment
					yet (migration 020). Saving is disabled until it is
					applied.
				</Alert>
			)}

			{SECTIONS.map(({ key, title, guide }) => {
				const unsaved = drafts[key] !== saved[key];
				const sectionMeta = meta[key];
				return (
					<Paper key={key} variant="outlined" sx={{ p: 3, mb: 3 }}>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 1,
								mb: 1,
							}}
						>
							<Typography variant="h6">{title}</Typography>
							{unsaved && (
								<Chip
									size="small"
									color="warning"
									label="Unsaved changes"
								/>
							)}
						</Box>
						<Typography
							variant="body2"
							color="text.secondary"
							component="div"
							sx={{ mb: 2 }}
						>
							AASB S2 requires this section to disclose:
							<ul style={{ margin: "6px 0 0", paddingLeft: 20 }}>
								{guide.map((g) => (
									<li key={g}>{g}</li>
								))}
							</ul>
						</Typography>
						<TextField
							fullWidth
							multiline
							minRows={8}
							value={drafts[key]}
							onChange={(e) =>
								setDrafts((prev) => ({
									...prev,
									[key]: e.target.value,
								}))
							}
							placeholder="Write the narrative here. Leave empty to keep the guided placeholder in the report."
							helperText="Plain text — a blank line starts a new paragraph."
							disabled={loading || !available}
						/>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 1,
								mt: 1.5,
							}}
						>
							<Typography variant="caption" color="text.secondary">
								{sectionMeta
									? `v${sectionMeta.version} · saved ${
											sectionMeta.updatedBy
												? `by ${sectionMeta.updatedBy} `
												: ""
										}on ${formatWhen(sectionMeta.updatedAt)}`
									: "Not written yet — the report shows the guided placeholder."}
							</Typography>
							<Box sx={{ flex: 1 }} />
							<Button
								size="small"
								startIcon={<HistoryOutlinedIcon />}
								disabled={!sectionMeta}
								onClick={() => openHistory(key, title)}
							>
								History
							</Button>
							<Button
								size="small"
								variant="contained"
								startIcon={
									busy === key ? (
										<CircularProgress
											size={16}
											color="inherit"
										/>
									) : (
										<SaveOutlinedIcon />
									)
								}
								disabled={
									!available ||
									busy !== null ||
									loading ||
									!unsaved
								}
								onClick={() => save(key)}
							>
								Save
							</Button>
						</Box>
					</Paper>
				);
			})}

			<Dialog
				open={history !== null}
				onClose={() => setHistory(null)}
				fullWidth
				maxWidth="md"
			>
				<DialogTitle>
					{history?.title} — history ({year})
				</DialogTitle>
				<DialogContent dividers>
					{history?.loading ? (
						<Box sx={{ textAlign: "center", py: 3 }}>
							<CircularProgress size={24} />
						</Box>
					) : (
						history?.versions.map((v) => (
							<Paper
								key={v.version}
								variant="outlined"
								sx={{ p: 2, mb: 2 }}
							>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 1,
										mb: 1,
									}}
								>
									<Typography variant="subtitle2">
										v{v.version}
										{v.updatedBy ? ` · ${v.updatedBy}` : ""}
										{` · ${formatWhen(v.createdAt)}`}
									</Typography>
									<Box sx={{ flex: 1 }} />
									<Button
										size="small"
										onClick={() => restore(v)}
									>
										Load into editor
									</Button>
								</Box>
								<Typography
									variant="body2"
									sx={{ whiteSpace: "pre-wrap" }}
								>
									{v.content || (
										<em>(cleared — placeholder shown)</em>
									)}
								</Typography>
							</Paper>
						))
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setHistory(null)}>Close</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default DisclosureWorkspace;
